import React, {Fragment, useEffect, useRef} from 'react';
import type {MutableRefObject, RefObject} from 'react';

import CSSModules from 'react-css-modules';
import {flushSync} from 'react-dom';
import Moveable from 'react-moveable';
import type {
  OnDrag,
  OnDragEnd,
  OnRotate,
  OnRotateEnd,
  OnRotateStart,
  OnScaleEnd,
  OnScaleStart,
} from 'react-moveable';

import {MoveableObject} from './MoveableObject';
import type {ObjectToolbarProps} from './ObjectToolbar/ObjectToolbar';
import {ObjectToolbar} from './ObjectToolbar/ObjectToolbar';
import type {BaseObject, CanvasSize} from './types';

import styles from './styles.module.scss';

export const CANVAS_RATIO = {
  width: 9,
  height: 16,
};

const MIN_OBJECT_SCALE = 0.1;

export type Props<T extends BaseObject> = {
  /**
   * The HTML element that is currently selected
   */
  selectedTarget: MutableRefObject<HTMLElement> | null;
  setSelectedTarget: (target: MutableRefObject<HTMLElement> | null) => void;
  /**
   * The object that is currently selected. This is the underlying data structure representation of the currently selected HTML element (`selectedTarget`).
   */
  selectedObject: T | null;
  setSelectedObject: (object: T | null) => void;
  /**
   * The ref to assign to the Moveable instance
   */
  moveableRef: RefObject<Moveable>;
  /**
   * The ref to assign to the canvas container.
   * If you don't provide a ref then one will be created locally, but providing one can be helpful
   * in cases where you want to access the container in the place it's rendered.
   */
  canvasContainerRef?: MutableRefObject<HTMLDivElement | null>;
  /**
   * The callback function that is called when the canvas is setup
   */
  onCanvasSetup?: (container: HTMLDivElement | null) => void;
  /**
   * The opacity of the container
   */
  containerOpacity?: number;
  /**
   * The objects to render on the canvas
   */
  moveableObjects: T[];
  /**
   * If enabled, will dynamically adjust the position and scale of the object to prevent it from overflowing the canvas.
   * You may wish to disable this as a performance optimization if you have no need for such calculations,
   * e.g. in Guides where multilingual text is not supported.
   */
  objectPlacementEnabled?: boolean;
  /**
   * The callback function called when an object on the canvas is clicked
   */
  handleObjectMouseDown: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: T,
    containerRef: MutableRefObject<HTMLDivElement>
  ) => void;
  /**
   * The callback function called when an object on the canvas is double-clicked
   */
  handleObjectDoubleClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: T
  ) => void;
  /**
   * The callback function that is called when an object on the canvas is moved, rotated, or scaled.
   * The consuming component should use this prop to access the current positioning of the object.
   * In addition to returning the current positioning of the object, the callback also receives an event type ('start' or 'end') to indicate
   * whether the event is fired at the beginning or end of the layout change. This distinction can be useful if you have computationally expensive operations
   * that you only want to perform at the end of the layout change.
   */
  handleObjectLayoutChange: (
    event: 'start' | 'end',
    style: Partial<T['styles']>
  ) => void;
  /**
   * The size of the canvas. You may omit this if you'd like the canvas to take up the full width and height of the container.
   */
  canvasSize?: CanvasSize;
  /**
   * Props to pass to the object toolbar
   */
  toolbarProps?: ObjectToolbarProps;
  /**
   * When true, removes ability to move objects on canvas
   */
  disableMoveableInteractions?: boolean;
  /**
   * Reduces the values of certain styles on `moveableObject`s - such as padding -
   * to make them less prominent in smaller preview sizes.
   */
  isPreviewMode?: boolean;
  /**
   * Children to render adjacent to the Moveable instance and the objects rendered on the canvas.
   * Useful if the canvas needs to support other functionality such as line drawing within Guides,
   * which uses this prop to render the Sketch component.
   */
  children?: React.ReactNode;
  /**
   * Optional styles to apply to the canvas container
   */
  containerStyle?: React.CSSProperties;
  /**
   * Placeholder text for dynamic text objects
   */
  dynamicTextPlaceholder?: string;
};

// eslint-disable-next-line complexity
const EditorCanvasComponent = <T extends BaseObject>({
  selectedTarget,
  setSelectedTarget,
  selectedObject,
  children,
  onCanvasSetup,
  canvasContainerRef,
  moveableRef,
  containerOpacity = 0,
  moveableObjects,
  objectPlacementEnabled = true,
  handleObjectMouseDown,
  handleObjectDoubleClick,
  handleObjectLayoutChange,
  canvasSize,
  toolbarProps,
  setSelectedObject,
  disableMoveableInteractions = false,
  isPreviewMode = false,
  containerStyle,
  dynamicTextPlaceholder,
}: Props<T>) => {
  const localContainerRef = useRef<HTMLDivElement>(null);
  const containerRef: MutableRefObject<HTMLDivElement | null> =
    canvasContainerRef ?? localContainerRef;
  const width =
    canvasSize?.width ?? containerRef.current?.getBoundingClientRect().width;
  const height =
    canvasSize?.height ?? containerRef.current?.getBoundingClientRect().height;

  const handleUpdateStyles = (
    event: 'start' | 'end',
    style: Record<string, unknown>
  ) => {
    if (!selectedObject) {
      return;
    }

    handleObjectLayoutChange(event, {...selectedObject.styles, ...style});
  };

  useEffect(() => {
    if (!selectedObject) {
      setSelectedTarget(null);
    }
  }, [selectedObject, setSelectedTarget]);

  return (
    <>
      <div
        styleName='object-editor'
        ref={(obj) => {
          containerRef.current = obj;
          onCanvasSetup?.(obj);
        }}
        style={{
          opacity: containerOpacity,
          ...containerStyle,
        }}
        data-automation='canvas-container'
      >
        <div className='safe-area' />
        {!disableMoveableInteractions && width && height && (
          <Moveable
            ref={moveableRef}
            flushSync={flushSync}
            target={selectedTarget}
            origin={false}
            edge={true}
            draggable={true}
            scalable={true}
            rotatable={true}
            throttleDrag={0}
            throttleScale={0}
            rotationPosition={objectPlacementEnabled ? 'bottom-right' : 'top'}
            keepRatio={true}
            snappable={true}
            snapDirections={{
              center: true,
              middle: true,
              left: true,
              right: true,
              top: true,
              bottom: true,
            }}
            verticalGuidelines={[width / 15, width / 2, (width * 14) / 15]}
            horizontalGuidelines={[height / 15, height / 2, (height * 14) / 15]}
            bounds={{
              top: 0,
              left: 0,
              right: containerRef.current?.getBoundingClientRect().width,
              bottom: containerRef.current?.getBoundingClientRect().height,
            }}
            onDragStart={({set}) => {
              if (selectedObject) {
                set([
                  selectedObject.styles.x ?? 0,
                  selectedObject.styles.y ?? 0,
                ]);
              }
            }}
            onDrag={({top, left, target}: OnDrag) => {
              handleUpdateStyles('start', {
                x: (left / width) * CANVAS_RATIO.width,
                y: (top / height) * CANVAS_RATIO.height,
              });
              /** This is necessary for smooth movement */
              target.style.top = `${top}px`;
              target.style.left = `${left}px`;
            }}
            onDragEnd={(e: OnDragEnd) => {
              if (!e.lastEvent || !selectedObject) {
                return;
              }

              // react-moveable types OnDragEnd.lastEvent as `any`
              if ('top' in e.lastEvent && 'left' in e.lastEvent) {
                const {top, left} = e.lastEvent as {top: number; left: number};

                handleUpdateStyles('end', {
                  x: (left / width) * CANVAS_RATIO.width,
                  y: (top / height) * CANVAS_RATIO.height,
                });
              }
            }}
            onRotateStart={({set}: OnRotateStart) => {
              if (selectedObject) {
                set(selectedObject.styles.rotate ?? 0);
              }
            }}
            onRotate={({
              beforeRotate,
              drag: {top, left},
              target,
              transform,
            }: OnRotate) => {
              handleUpdateStyles('start', {
                x: (left / width) * CANVAS_RATIO.width,
                y: (top / height) * CANVAS_RATIO.height,
                rotate: beforeRotate,
              });
              /** This is necessary for smooth movement */
              target.style.transform = transform;
            }}
            onRotateEnd={({lastEvent}: OnRotateEnd) => {
              if (!lastEvent) {
                return;
              }

              if ('beforeRotate' in lastEvent) {
                const {beforeRotate} = lastEvent as {beforeRotate: number};

                handleUpdateStyles('end', {
                  rotate: beforeRotate,
                });
              }
            }}
            onScaleStart={({set}: OnScaleStart) => {
              if (selectedObject) {
                set([
                  selectedObject.styles.scale ?? 1,
                  selectedObject.styles.scale ?? 1,
                ]);
              }
            }}
            onScale={({scale, target, transform}) => {
              // Prevent scaling so low that the object inverts
              let scaleY = scale[1];

              if (typeof scaleY === 'number' && scaleY < MIN_OBJECT_SCALE) {
                scaleY = MIN_OBJECT_SCALE;
                const currentRotation = selectedObject?.styles?.rotate ?? 0;
                target.style.transform = `scale(${scaleY}) rotate(${currentRotation}deg)`;
              } else {
                target.style.transform = transform;
              }

              handleUpdateStyles('start', {
                scale: scaleY,
              });
            }}
            onScaleEnd={({lastEvent}: OnScaleEnd) => {
              if (!lastEvent) {
                return;
              }

              if ('scale' in lastEvent) {
                const {scale} = lastEvent as {scale: number[]};
                // Prevent scaling so low that the object inverts
                const scaleY = Math.max(
                  scale[1] || MIN_OBJECT_SCALE,
                  MIN_OBJECT_SCALE
                );

                handleUpdateStyles('end', {
                  scale: scaleY,
                });
              }
            }}
          />
        )}
        {width && height && (
          <>
            {children}
            {moveableObjects.map((object) => (
              <Fragment key={object.id}>
                {selectedObject &&
                toolbarProps &&
                object.id === selectedObject.id ? (
                  <ObjectToolbar {...toolbarProps} selectedObject={object} />
                ) : null}
                <MoveableObject<T>
                  object={object}
                  selectedObject={selectedObject}
                  handleMouseDown={handleObjectMouseDown}
                  handleDoubleClick={handleObjectDoubleClick}
                  canvasSize={{width, height}}
                  setSelectedObject={setSelectedObject}
                  objectPlacementEnabled={objectPlacementEnabled}
                  isPreviewMode={isPreviewMode}
                  dynamicTextPlaceholder={dynamicTextPlaceholder}
                />
              </Fragment>
            ))}
          </>
        )}
      </div>
    </>
  );
};

export const EditorCanvas = CSSModules(EditorCanvasComponent, styles, {
  allowMultiple: true,
});
